import * as React from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { History, Mail } from "lucide-react";
import { ProgressCTA } from "@/components/home/ProgressCTA/ProgressCTA";
import { ChangelogCard } from "@/components/cards/changelog/ChangelogCard";
import { Heading } from "@/components/ui/heading";
import { type ChangelogEntry, getChangelog } from "@/lib/changelog/getChangelog";
import { WHATSAPP_COMMUNITY_URL } from "@/config/constants";
import { PATHS } from "@/config/paths";
import { seo } from "@/utilities/seo";

export const Route = createFileRoute("/_home/changelog")({
  loader: async () => {
    return { changelog: await getChangelog() };
  },
  head: () => ({
    meta: [
      { title: "Changelog - ProgressMade.ai" },
      ...seo({
        title: "Changelog - ProgressMade.ai",
        description: "Stay up-to-date with the latest improvements and updates to ProgressMade.ai.",
        keywords:
          "changelog, health, fitness, wellness, planning, health habits, structure, streaks, 75 hard, Bryan Johnson",
        image: "https://progressmade.ai/meta-image.webp",
        canonical: "https://progressmade.ai/changelog",
      }),
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { changelog } = Route.useLoaderData();
  const router = useRouter();

  const requestFeature = () => {
    window.open(WHATSAPP_COMMUNITY_URL, "_blank");
  };

  return (
    <>
      <div className="col-span-12 md:col-span-9 md:col-start-2 xl:col-span-8 xl:col-start-3">
        <div className="flex flex-col items-center gap-4 mb-6 md:mb-12 text-center">
          <History className="h-8 w-8 text-gray-700" />

          <Heading level="h1" size="h1">
            Changelog
          </Heading>

          <p className="max-w-xl text-lg text-pretty leading-8 text-foreground">
            Stay up-to-date with the latest improvements and updates to{" "}
            <span className="text-primary">ProgressMade</span>. We regularly enhance our platform
            with new features, performance improvements, and bug fixes to provide you with the best
            possible experience on your health and fitness journey.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <button
              type="button"
              onClick={requestFeature}
              className="bg-primary text-white rounded-full px-6 py-4 font-semibold hover:bg-primary/90 transition-colors cursor-pointer"
            >
              Request New Feature
            </button>
            <button
              type="button"
              onClick={() => router.navigate({ to: PATHS.NEWSLETTER })}
              className="flex items-center gap-2 text-black bg-[#f0f0f4] rounded-full px-6 py-4 font-semibold hover:bg-[#e5e5e9] transition-colors cursor-pointer"
            >
              <Mail className="w-4 h-4" />
              Join Newsletter
            </button>
          </div>
        </div>

        <div className="relative">
          {changelog.length > 0 && (
            <div className="timeline-container">
              {/* Continuous vertical line */}
              <div
                className="absolute left-6 w-[2px] bg-border/80"
                style={{
                  top: "30px",
                  bottom: "0",
                  height: "calc(100% - 28px)",
                }}
              />

              <div className="space-y-8">
                {changelog.map((entry: ChangelogEntry, index: number) => (
                  <div key={entry.slug} className="relative">
                    {/* Timeline dot */}
                    <div className="absolute left-[25px] top-8 w-[10px] h-[10px] bg-primary rounded-full transform -translate-x-1/2 z-10 ring-2 ring-white dark:ring-background" />

                    {/* Timeline content */}
                    <div className="ml-10">
                      <ChangelogCard entry={entry} className="rounded-lg px-4" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ProgressCTA />
    </>
  );
}
